import { portalApi, TAGS } from "api/rtkApi";
import { SERVICES } from "constants/environment";
import { ObjectWithId } from "utils/ObjectWithId";

import type { CountryCode } from "models/CountryCode";
import type { UUID } from "utils";

import type { CashManagementAccount } from "../models/CashManagementAccount";
import type { CreateCashManagementAccountForm } from "../models/CreateCashManagementAccountForm";

type GetCashManagementAccountResponse = CashManagementAccount;
type GetCashManagementAccountRequest = ObjectWithId;

type PutCashManagementAccountResponse = ObjectWithId;
type PutCashManagementAccountRequest = ObjectWithId & {
  data: Partial<CashManagementAccount>;
};

type PostCashManagementAccountResponse = ObjectWithId;
export type PostCashManagementAccountRequest =
  CreateCashManagementAccountForm & {
    customerId: UUID;
    country?: CountryCode;
  };

const extendedApi = portalApi.injectEndpoints({
  endpoints: (builder) => ({
    getCashManagementAccount: builder.query<
      GetCashManagementAccountResponse,
      GetCashManagementAccountRequest
    >({
      query: ({ id }) => ({
        url: `cash-management-accounts/${id}`,
      }),
      providesTags: [TAGS.CASH_MANAGEMENT_ACCOUNTS],
      extraOptions: {
        service: SERVICES.CONNECT,
      },
    }),
    putCashManagementAccount: builder.mutation<
      PutCashManagementAccountResponse,
      PutCashManagementAccountRequest
    >({
      query: ({ id, data }) => ({
        url: `/cash-management-accounts/${id}`,
        method: "PUT",
        body: data,
      }),
      invalidatesTags: [TAGS.CASH_MANAGEMENT_ACCOUNTS],
      extraOptions: {
        service: SERVICES.CONNECT,
      },
      onQueryStarted: async ({ id, data }, { dispatch, queryFulfilled }) => {
        const patchResult = dispatch(
          extendedApi.util.updateQueryData(
            "getCashManagementAccount",
            { id },
            (draft) => {
              Object.assign(draft, data);
            }
          )
        );

        try {
          await queryFulfilled;
        } catch {
          patchResult.undo();
        }
      },
    }),
    postCashManagementAccount: builder.mutation<
      PostCashManagementAccountResponse,
      PostCashManagementAccountRequest
    >({
      query: (data) => ({
        url: "/cash-management-accounts",
        method: "POST",
        body: preparePostCashManagementAccountDataForApi(data),
      }),
      invalidatesTags: [TAGS.CASH_MANAGEMENT_ACCOUNTS],
      extraOptions: {
        service: SERVICES.CONNECT,
      },
    }),
  }),
});

export const {
  useGetCashManagementAccountQuery,
  usePutCashManagementAccountMutation,
  usePostCashManagementAccountMutation,
} = extendedApi;

function preparePostCashManagementAccountDataForApi(
  data: PostCashManagementAccountRequest
) {
  return {
    nickname: data.nickname,
    currency: data.currency?.id,
    country: data.country,
    cashManagementProviderAccount: {
      settlementEnabled: data.settlementEnabled,
      cashManagementProvider: data.cashManagementProvider?.id,
      additionalConfiguration: data.additionalConfiguration,
    },
    customer: data.customerId,
    ...(data.bsb && {
      bsb: data.bsb,
    }),
    ...(data.auAccountNumber && {
      auAccountNumber: data.auAccountNumber,
    }),
    ...(data.iban && {
      iban: data.iban,
    }),
    ...(data.swiftBic && {
      swiftBic: data.swiftBic,
    }),
    ...(data.accountNumber && {
      accountNumber: data.accountNumber,
    }),
    ...(data.routing && {
      routing: data.routing,
    }),
  };
}
